/* eslint-disable camelcase */
import { z } from 'zod'

import { type AttributionRow, RESOLUTION_SLOTS } from './artifactDerivations'

import type { SpeakerAttributionProvenance, SpeakerMap } from '@/api/generated'

const stageSchema = z.object({
  stage: z.string(),
  outcome: z.string(),
  reason: z.string().nullable(),
  duration_ms: z.number().int().nonnegative(),
  variant: z.string().nullable(),
  fallback_from: z.string().nullable(),
  arm: z.string().nullish(),
})

const diagnosticsSchema = z.object({
  status: z.enum(['available', 'unavailable']),
  reason: z.string().nullable(),
  stages: z.array(stageSchema),
  speakers: z.array(
    z.object({
      canonical_id: z.number().int().nonnegative(),
      person_id: z.string(),
      slot: z.enum(['dom', 'mic']),
      stage: z.string(),
      via: z.string(),
    })
  ),
})

export type AttributionDiagnostics = z.infer<typeof diagnosticsSchema>

export function parseAttributionDiagnostics(response: unknown, revision: string | null): AttributionDiagnostics | null {
  const parsed = z
    .object({
      displayed_revision: z.string().nullable(),
      diagnostics: diagnosticsSchema,
    })
    .safeParse(response)
  if (!parsed.success) return null
  if (revision === null && parsed.data.displayed_revision === null)
    return { status: 'unavailable', reason: 'not_recorded', stages: [], speakers: [] }
  if (!revision || parsed.data.displayed_revision !== revision)
    return {
      status: 'unavailable',
      reason: 'revision_mismatch',
      stages: [],
      speakers: [],
    }
  return parsed.data.diagnostics
}

const METHOD_NAMES: Record<string, string> = {
  time: 'AX timing',
  words: 'Mic content match',
  only_voice: 'Sole mic speaker',
  provider_mic: 'Provider mic channel',
  contested: 'Owner conflict exemption',
  attendee: 'Attendee content match',
  paired: 'Paired attendee',
  remaining: 'Last remaining speaker',
  relabel: 'GradientBoost',
  rescue: 'Classical rescue',
} satisfies Record<SpeakerAttributionProvenance['via'], string>

const STAGE_NAMES: Record<string, string> = {
  assigner: 'Assigner',
  gap_fill: 'Gap fill',
  relabeler: 'Relabeler',
  classical_rescue: 'Classical rescue',
} satisfies Record<SpeakerAttributionProvenance['stage'], string>

export function withAttributionMethods(
  rows: AttributionRow[],
  speakerMap: SpeakerMap | null | undefined,
  diagnostics: AttributionDiagnostics | null,
  isAssigner = false
): AttributionRow[] {
  const claims = new Map(
    (diagnostics?.status === 'available' ? diagnostics.speakers : []).map((claim) => [
      `${claim.canonical_id}:${claim.slot}:${claim.person_id}`,
      claim,
    ])
  )
  return rows.map((row) => {
    const id = row.speaker?.speakerId
    if (id == null) return row
    const assignment = speakerMap?.assignments?.[String(id)]
    const slots = isAssigner ? (['dom', 'mic'] as const) : RESOLUTION_SLOTS
    const slot = slots.find((candidate) => assignment?.[candidate] != null)
    const personId = slot ? assignment?.[slot] : null
    let method: string | undefined
    if (diagnostics?.reason !== 'revision_mismatch') {
      if (slot === 'user') method = 'Manual label'
      else if (slot === 'llm') method = 'LLM naming'
      else if (slot === 'dom' || slot === 'mic') {
        const claim = claims.get(`${id}:${slot}:${personId}`)
        if (claim) {
          const source = METHOD_NAMES[claim.via]
          const stage = STAGE_NAMES[claim.stage]
          if (source && stage)
            method = claim.stage === 'classical_rescue' && claim.via === 'rescue' ? stage : `${stage} · ${source}`
        }
      } else if (!row.speaker?.name && !slot) method = 'Unlabeled'
    }
    return { ...row, method }
  })
}
